// useRoomSocket: a single WebSocket to /ws that holds the authoritative
// room state (playback, participants, chat, browser, screen).
//
// Exposes typed send methods that build messages with command/message IDs.

import { useEffect, useRef, useState, useCallback } from "react";
import type {
  ServerMessage,
  ClientMessage,
  PlaybackState,
  Participant,
  ChatMessageMsg,
  BrowserInfo,
  DetectedStream
} from "@shared/protocol";
import { makeId, loadOrCreateName, saveName } from "../lib/displayName.js";

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 8000;

export interface RoomState {
  connected: boolean;
  playback: PlaybackState | null;
  participants: Participant[];
  chat: ChatMessageMsg[];
  browser: BrowserInfo;
  detectedStreams: DetectedStream[];
  screenTicket: string | null;
  /** Bumped by the server every snapshot. Use to dedupe re-applies. */
  appliedRevision: number;
}

export interface RoomActions {
  loadSource: (source: Extract<ClientMessage, { type: "loadSource" }>["source"]) => void;
  play: () => void;
  pause: () => void;
  seek: (currentTime: number) => void;
  sendChat: (text: string) => void;
  setName: (name: string) => void;
  browserOpen: (startUrl?: string) => void;
  browserNavigate: (url: string) => void;
  browserAction: (action: "back" | "forward" | "reload") => void;
  browserMouse: (event: "mouseMoved" | "mousePressed" | "mouseReleased", x: number, y: number, opts?: { button?: "left" | "middle" | "right"; buttons?: number; clickCount?: number; modifiers?: number }) => void;
  browserWheel: (x: number, y: number, deltaX: number, deltaY: number) => void;
  browserKey: (event: "keyDown" | "keyUp", key: string, code: string, opts?: { text?: string; modifiers?: number; autoRepeat?: boolean }) => void;
  browserInsertText: (text: string) => void;
  browserClose: () => void;
  /** Send a free-form syncReport (player calls this periodically). */
  sendSyncReport: (data: {
    currentTime: number;
    isPlaying: boolean;
    readyState: number;
    buffering: boolean;
    sourceRevision: number;
  }) => string;
}

export interface UseRoomSocketResult {
  state: RoomState;
  actions: RoomActions;
  /** Last syncDirective from server, for the player to consume. */
  syncDirective: import("@shared/protocol").SyncDirectiveMsg | null;
  /** Internal — fired when a binary screen frame is received. */
  onScreenFrame: (cb: (data: ArrayBuffer) => void) => () => void;
}

function buildWsUrl(): string {
  const loc = window.location;
  const proto = loc.protocol === "https:" ? "wss:" : "ws:";
  // In dev, Vite's proxy for /ws is unreliable. Bypass it and connect
  // directly to the Node server's port. Use the page's hostname so a
  // remote client (e.g. on the LAN) reaches the actual server host
  // rather than its own loopback. When the page itself was loaded from
  // `localhost`/`::1`, force IPv4 127.0.0.1: some Firefox configurations
  // resolve `localhost` to IPv6 `::1` first while the server binds IPv4
  // `0.0.0.0`, which silently fails the upgrade. WebSocket has no CORS
  // and the server accepts any Origin. In production the server and
  // client share an origin so the URL stays relative.
  const isViteDev = loc.port === "5173";
  if (isViteDev) {
    const h = loc.hostname;
    const host = h === "localhost" || h === "::1" ? "127.0.0.1" : h;
    return `${proto}//${host}:3000/ws`;
  }
  return `${proto}//${loc.host}/ws`;
}

export function useRoomSocket(): UseRoomSocketResult {
  const [name, setNameState] = useState<string>(() => loadOrCreateName());
  const [state, setState] = useState<RoomState>({
    connected: false,
    playback: null,
    participants: [],
    chat: [],
    browser: { status: "closed", sessionId: null, activeUrl: null, viewport: null },
    detectedStreams: [],
    screenTicket: null,
    appliedRevision: -1
  });
  const [syncDirective, setSyncDirective] = useState<import("@shared/protocol").SyncDirectiveMsg | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
  const screenListeners = useRef<Set<(data: ArrayBuffer) => void>>(new Set());

  const send = useCallback((msg: ClientMessage) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify(msg));
  }, []);

  const sendSyncReport: RoomActions["sendSyncReport"] = useCallback((data) => {
    const id = makeId();
    send({
      type: "syncReport",
      requestId: id,
      sourceRevision: data.sourceRevision,
      currentTime: data.currentTime,
      isPlaying: data.isPlaying,
      readyState: data.readyState,
      buffering: data.buffering,
      clientSentAt: Date.now()
    });
    return id;
  }, [send]);

  // Connect effect — StrictMode-safe: closure-scoped cancellation flag,
  // so the simulated unmount/remount can't strand a second WS.
  useEffect(() => {
    let cancelled = false;
    let reconnectTimerLocal: ReturnType<typeof setTimeout> | null = null;

    const scheduleReconnect = () => {
      if (cancelled) return;
      const delay = Math.min(
        RECONNECT_MAX_MS,
        RECONNECT_BASE_MS * Math.pow(2, reconnectAttempt.current)
      );
      reconnectAttempt.current++;
      reconnectTimerLocal = setTimeout(doConnect, delay);
    };

    const doConnect = () => {
      if (cancelled) return;
      const url = buildWsUrl();
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.binaryType = "arraybuffer";

      ws.onopen = () => {
        if (cancelled) {
          // Effect was cleaned up before we got the open event — drop it.
          try { ws.close(); } catch { /* ignore */ }
          return;
        }
        reconnectAttempt.current = 0;
        ws.send(
          JSON.stringify({
            type: "join",
            clientId: makeId(),
            name
          } satisfies ClientMessage)
        );
        setState((s) => ({ ...s, connected: true }));
      };

      ws.onmessage = (evt) => {
        if (typeof evt.data === "string") {
          let parsed: ServerMessage;
          try {
            parsed = JSON.parse(evt.data) as ServerMessage;
          } catch {
            return;
          }
          handleServerMessage(parsed, setState, setSyncDirective);
        } else if (evt.data instanceof ArrayBuffer) {
          const view = new Uint8Array(evt.data);
          if (view.length < 1) return;
          const kind = view[0];
          if (kind === 0x53) {
            const payload = view.subarray(1);
            const ab = payload.buffer.slice(
              payload.byteOffset,
              payload.byteOffset + payload.byteLength
            );
            for (const cb of screenListeners.current) {
              try {
                cb(ab);
              } catch {
                // ignore
              }
            }
          }
        }
      };

      ws.onclose = () => {
        setState((s) => ({ ...s, connected: false }));
        if (cancelled) return;
        scheduleReconnect();
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };

    doConnect();

    return () => {
      cancelled = true;
      if (reconnectTimerLocal) clearTimeout(reconnectTimerLocal);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      const ws = wsRef.current;
      wsRef.current = null;
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, [name]);

  const actions: RoomActions = {
    loadSource: (source) =>
      send({
        type: "loadSource",
        commandId: makeId(),
        source
      }),
    play: () => send({ type: "play", commandId: makeId(), sourceRevision: state.playback?.sourceRevision ?? 0 }),
    pause: () => send({ type: "pause", commandId: makeId(), sourceRevision: state.playback?.sourceRevision ?? 0 }),
    seek: (currentTime) =>
      send({
        type: "seek",
        commandId: makeId(),
        sourceRevision: state.playback?.sourceRevision ?? 0,
        currentTime
      }),
    sendChat: (text) =>
      send({ type: "chatSend", messageId: makeId(), text }),
    setName: (newName) => {
      const trimmed = newName.trim().slice(0, 32);
      if (!trimmed) return;
      saveName(trimmed);
      setNameState(trimmed);
      send({ type: "setName", name: trimmed });
    },
    browserOpen: (startUrl) => {
      const msg: ClientMessage = startUrl
        ? { type: "browserOpen", startUrl }
        : { type: "browserOpen" };
      send(msg);
    },
    browserNavigate: (url) => send({ type: "browserNavigate", url }),
    browserAction: (action) => send({ type: "browserAction", action }),
    browserMouse: (event, x, y, opts) =>
      send({
        type: "browserMouse",
        event,
        x,
        y,
        button: opts?.button,
        buttons: opts?.buttons,
        clickCount: opts?.clickCount,
        modifiers: opts?.modifiers
      }),
    browserWheel: (x, y, deltaX, deltaY) =>
      send({ type: "browserWheel", x, y, deltaX, deltaY }),
    browserKey: (event, key, code, opts) =>
      send({
        type: "browserKey",
        event,
        key,
        code,
        text: opts?.text,
        modifiers: opts?.modifiers,
        autoRepeat: opts?.autoRepeat
      }),
    browserInsertText: (text) => send({ type: "browserInsertText", text }),
    browserClose: () => send({ type: "browserClose" }),
    sendSyncReport
  };

  const onScreenFrame = useCallback((cb: (data: ArrayBuffer) => void) => {
    screenListeners.current.add(cb);
    return () => {
      screenListeners.current.delete(cb);
    };
  }, []);

  return { state, actions, syncDirective, onScreenFrame };
}

function handleServerMessage(
  msg: ServerMessage,
  setState: React.Dispatch<React.SetStateAction<RoomState>>,
  setSyncDirective: React.Dispatch<React.SetStateAction<import("@shared/protocol").SyncDirectiveMsg | null>>
): void {
  switch (msg.type) {
    case "welcome":
      setState((s) => ({
        ...s,
        connected: true,
        playback: msg.envelope.state,
        participants: msg.participants,
        chat: msg.chatHistory,
        browser: msg.browser,
        screenTicket: msg.screenTicket,
        appliedRevision: msg.envelope.state.revision
      }));
      break;
    case "stateSnapshot":
      setState((s) => {
        // Ignore out-of-order snapshots.
        if (msg.envelope.state.revision < s.appliedRevision) return s;
        return {
          ...s,
          playback: msg.envelope.state,
          appliedRevision: msg.envelope.state.revision
        };
      });
      break;
    case "syncDirective":
      setSyncDirective(msg);
      break;
    case "chatMessage":
      setState((s) => ({ ...s, chat: [...s.chat, msg].slice(-200) }));
      break;
    case "presence":
      setState((s) => {
        const next = [...s.participants];
        const idx = next.findIndex((p) => p.clientId === msg.participant.clientId);
        if (msg.event === "left") {
          if (idx !== -1) next.splice(idx, 1);
        } else {
          if (idx === -1) next.push(msg.participant);
          else next[idx] = msg.participant;
        }
        return { ...s, participants: next };
      });
      break;
    case "browserStatus":
      setState((s) => ({
        ...s,
        browser: {
          status: msg.status,
          sessionId: msg.sessionId,
          activeUrl: msg.activeUrl ?? s.browser.activeUrl,
          viewport: msg.viewport ?? s.browser.viewport
        }
      }));
      break;
    case "browserScreenInfo":
      setState((s) => ({
        ...s,
        browser: { ...s.browser, viewport: { width: msg.width, height: msg.height } }
      }));
      break;
    case "streamDetected":
      console.log("[ws] streamDetected: count=", msg.streams.length, "urls=", msg.streams.map((s) => s.displayUrl));
      setState((s) => ({ ...s, detectedStreams: msg.streams }));
      break;
    case "browserUrlChanged":
      setState((s) => ({ ...s, browser: { ...s.browser, activeUrl: msg.url } }));
      break;
    case "browserClosed":
      setState((s) => ({
        ...s,
        browser: { status: "closed", sessionId: null, activeUrl: null, viewport: s.browser.viewport },
        detectedStreams: []
      }));
      break;
    case "sourceError":
    case "error":
      console.warn("[ws] server error", msg);
      break;
  }
}